/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import './MobileToggleField.scss';

import { Stack, StackItem } from '@fluentui/react/lib/Stack';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { CustomizerContext } from '@fluentui/react/lib/Utilities';
import { useId } from '@fluentui/react-hooks';
import * as React from 'react';
import { getThemedIcon, IconName } from '../../../resources/icons/Icons';
import { MOBILE_FONT_SIZE } from '../../../utilities/FontUtils';
import { MobileActionTableItem } from '../MobileActionTableItem/MobileActionTableItem';
import { getMobileToggleStackStyles, getMobileToggleStyles } from './MobileToggleField.styles';

export enum MobileToggleFieldType {
    AllMustApprove,
    CustomResponse,
    NonDefaultEnvironment,
    InOrder,
}

interface IMobileToggleFieldElementProps {
    text: string;
    checked: boolean;
    disabled?: boolean;
    onChange?(ev: React.MouseEvent<HTMLElement>, checked: boolean): void;
}

interface IMobileToggleFieldProps extends IMobileToggleFieldElementProps {
    type: MobileToggleFieldType;
}

export const MobileToggleField: React.FC<IMobileToggleFieldProps> = (props: IMobileToggleFieldProps): JSX.Element => {
    let icon: JSX.Element;

    switch (props.type) {
        case MobileToggleFieldType.AllMustApprove:
            icon = getThemedIcon(IconName.checkMark);
            break;
        case MobileToggleFieldType.CustomResponse:
            icon = getThemedIcon(IconName.customResponse);
            break;
        case MobileToggleFieldType.NonDefaultEnvironment:
            icon = getThemedIcon(IconName.documentArrowRight);
            break;
        case MobileToggleFieldType.InOrder:
            icon = getThemedIcon(IconName.response);
    }
    const actionElement = <MobileToggle {...props} />;

    return <MobileActionTableItem unselectedIcon={icon} selectedIcon={icon} actionElement={actionElement} />;
};

export const MobileToggle: React.FC<IMobileToggleFieldElementProps> = (
    props: IMobileToggleFieldElementProps
): JSX.Element => {
    const theme = React.useContext(CustomizerContext).customizations.settings.theme;
    const { text, checked, disabled } = props;
    const labelId = useId();

    return (
        <div className={'toggle-field-container'}>
            <Stack horizontal horizontalAlign='space-between' styles={getMobileToggleStackStyles(theme)}>
                <StackItem>
                    <div style={{ fontSize: MOBILE_FONT_SIZE }} className='toggle-field-title' id={labelId}>
                        {text}
                    </div>
                </StackItem>
                <StackItem>
                    <Toggle
                        styles={getMobileToggleStyles()}
                        checked={checked}
                        disabled={disabled}
                        onChange={props.onChange}
                        aria-describedby={labelId}
                    />
                </StackItem>
            </Stack>
        </div>
    );
};
